import { Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { PendingAction as ActionType } from '@/src/lib/types';
import Sheet from './sheet';
import PendingAction from './pending-action';
import type { NotificationRow } from '@/src/hooks/useNotifications';

// Notification kinds that make up a staff member's request history.
export const REQUEST_KINDS = new Set(['request_sent', 'approval_done', 'approval_declined', 'approval_failed', 'shift_assigned', 'shift_removed']);

const STATUS: Record<string, { label: string; cls: string }> = {
  request_sent: { label: 'Waiting', cls: 'bg-honey-400/10 text-honey-400 border-honey-400/20' },
  approval_done: { label: 'Approved', cls: 'bg-leaf-400/10 text-leaf-text border-leaf-400/20' },
  approval_declined: { label: 'Declined', cls: 'bg-clay-400/10 text-clay-400 border-clay-400/20' },
  approval_failed: { label: "Couldn't be done", cls: 'bg-clay-400/10 text-clay-400 border-clay-400/20' },
  shift_assigned: { label: 'New shift', cls: 'bg-leaf-400/10 text-leaf-text border-leaf-400/20' },
  shift_removed: { label: 'Shift changed', cls: 'bg-honey-400/10 text-honey-400 border-honey-400/20' },
};

// One row per request: the newest update wins (Waiting -> Approved/Declined).
function latestPerRequest(rows: NotificationRow[]): NotificationRow[] {
  const seen = new Map<string, NotificationRow>();
  for (const n of rows) {
    const key = `${n.related_entity_id ?? n.id}:${n.kind === 'shift_assigned' || n.kind === 'shift_removed' ? n.kind : 'req'}`;
    const prev = seen.get(key);
    if (!prev || new Date(n.created_at) > new Date(prev.created_at)) seen.set(key, n);
  }
  return [...seen.values()].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
}

function ago(iso: string): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

interface RightSheetProps {
  isOpen: boolean;
  onClose: () => void;
  actions: ActionType[];
  onAction: (id: string, decision: 'accept' | 'reject' | 'forward' | 'cancel') => void;
  venueId?: string;
  userId?: string;
  isManager?: boolean;
  // Staff: their own request updates (from notifications).
  requests?: NotificationRow[];
}

export default function RightSheet({ isOpen, onClose, actions, onAction, venueId, userId, isManager, requests = [] }: RightSheetProps) {
  const myRequests = isManager ? [] : latestPerRequest(requests.filter((n) => REQUEST_KINDS.has(n.kind)));
  return (
    <Sheet isOpen={isOpen} onClose={onClose} side="right">
      <div className="flex flex-col h-full bg-ink/60 backdrop-blur-xl border-l border-cream-line lg:w-[320px]">
        <div className="p-6 border-b border-cream-line/50">
          <h2 className="text-sm font-semibold text-cream-muted uppercase tracking-wider">{isManager ? 'Tasks & Actions' : 'My requests'}</h2>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide">


          {/* Pending actions section — managers only */}
          {isManager && actions.length > 0 && (
            <div className="px-4 pb-6">
              <p className="text-xs font-bold text-cream-muted uppercase tracking-widest mb-3">Pending approvals</p>
              <AnimatePresence mode="popLayout">
                <div className="space-y-4">
                  {actions.map((action) => (
                    <PendingAction key={action.id} action={action} onAction={onAction} />
                  ))}
                </div>
              </AnimatePresence>
            </div>
          )}

          {/* My requests — staff */}
          {!isManager && myRequests.length > 0 && (
            <div className="px-4 py-4 space-y-3">
              {myRequests.map((n) => {
                const st = STATUS[n.kind] ?? STATUS.request_sent;
                return (
                  <div key={n.id} className="rounded-xl border border-cream-line p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${st.cls}`}>{st.label}</span>
                      <span className="text-[11px] text-cream-muted">{ago(n.created_at)}</span>
                    </div>
                    <p className="text-sm text-cream leading-snug">{n.body ?? n.title}</p>
                  </div>
                );
              })}
            </div>
          )}

          {!isManager && myRequests.length === 0 && (
            <div className="flex flex-col items-center justify-center pt-20 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-cream-muted/70 mb-4">
                <Check size={24} />
              </div>
              <h3 className="text-sm font-semibold text-cream">No requests yet</h3>
              <p className="text-xs text-cream-muted mt-1">When you ask for a shift swap or a task, you'll see the manager's answer here.</p>
            </div>
          )}

          {/* Empty state */}
          {(!venueId || !userId) && actions.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center pt-20 text-center px-6"
            >
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-cream-muted/70 mb-4">
                <Check size={24} />
              </div>
              <h3 className="text-sm font-semibold text-cream">All clear</h3>
              <p className="text-xs text-cream-muted mt-1">No tasks or actions pending.</p>
            </motion.div>
          )}
        </div>
      </div>
    </Sheet>
  );
}